<style scoped>
.planning-map {
	position: absolute;
	inset: 0;
}
.planning-map :deep(.maplibregl-map) {
	width: 100%;
	height: 100%;
}
</style>

<template>
	<div class="planning-map">
		<mgl-map
			:map-style="mapStyleURL(ALIDADE_SMOOTH)"
			:center="center"
			:zoom="6"
			@map:load="onLoad"
		>
			<SourceServiceArea
				layer-id="planning-service-area"
				@service-area-loaded="onServiceAreaLoaded"
			/>

			<!-- The Planning map is a tegola map, like every other map content
			     surface. `v` is not a configured param; it is a cache-buster so a
			     write that changes a footprint refetches the affected tiles. -->
			<SourceTegola
				id="planning-map"
				map-name="planning"
				:params="{ v: version }"
			>
				<!-- Work Order footprints, under the hypotheses. -->
				<mgl-fill-layer
					layer-id="planning-work-order-fill"
					source-layer="planning-work-order-cell"
					:paint="workOrderFillPaint as any"
				/>
				<mgl-line-layer
					layer-id="planning-work-order-outline"
					source-layer="planning-work-order-cell"
					:paint="workOrderLinePaint as any"
				/>

				<!-- Lead footprints. Open Leads are blue, closed ones grey. -->
				<mgl-fill-layer
					layer-id="planning-lead-fill"
					source-layer="planning-lead-cell"
					:paint="leadFillPaint as any"
				/>
				<mgl-line-layer
					layer-id="planning-lead-outline"
					source-layer="planning-lead-cell"
					:paint="leadLinePaint as any"
				/>

				<!-- Legacy Leads predate H3 and carry a Site point. -->
				<mgl-circle-layer
					layer-id="planning-lead-points"
					source-layer="planning-lead-point"
					:paint="leadPointPaint as any"
				/>

				<!-- Signal points on top: colour is the analytical effect; an
				     unreviewed Signal is blue-ringed because its class and effect
				     are not yet a judgement. -->
				<mgl-circle-layer
					layer-id="planning-signals"
					source-layer="planning-signal"
					:paint="signalPaint as any"
				/>
			</SourceTegola>

			<!-- The H3 authoring grid: the cells a click can land in, at the
			     resolution in force. It exists only while cells are being painted,
			     because that is the only moment a cell is a target. The grid is cut
			     from the viewport rather than streamed from tiles: it is an editing
			     surface whose cells must be hit-testable as individual features,
			     and a tile source ships them merged for painting. -->
			<mgl-geo-json-source
				v-if="mode === 'paint'"
				source-id="planning-h3-grid"
				:data="gridCellsFC"
			>
				<!-- A transparent fill, because an outline has no interior: without
				     it a click in the middle of a hexagon falls through to the
				     Signal painted beneath, and the operator paints the wrong cell. -->
				<mgl-fill-layer layer-id="planning-h3-hit" :paint="hitPaint as any" />
				<mgl-line-layer
					layer-id="planning-h3-grid-outline"
					:paint="gridLinePaint as any"
				/>
			</mgl-geo-json-source>

			<!-- The saved Leads' own cells, drawn at cell granularity so that an
			     overlap between the draft and an existing Lead is legible as the
			     same hexagon rather than as two shapes crossing. Only cells the
			     current grid covers are drawn: the footprints themselves are the
			     tile layer above. -->
			<mgl-geo-json-source
				v-if="mode === 'paint' && leadCellFeatures.features.length > 0"
				source-id="planning-h3-leads"
				:data="leadCellFeatures"
			>
				<mgl-fill-layer
					layer-id="planning-h3-lead-fill"
					:paint="leadCellFillPaint as any"
				/>
				<mgl-line-layer
					layer-id="planning-h3-lead-line"
					:paint="leadCellLinePaint as any"
				/>
			</mgl-geo-json-source>

			<!-- The draft footprint, above every saved footprint. A cell the draft
			     shares with an open Lead is red: the centre-map design warns on
			     overlap rather than blocking, and the warning has to be visible
			     where the operator is painting. -->
			<mgl-geo-json-source
				source-id="planning-draft-cells"
				:data="draftCellFeatures"
			>
				<mgl-fill-layer
					layer-id="planning-draft-fill"
					:paint="draftFillPaint as any"
				/>
				<mgl-line-layer
					layer-id="planning-draft-outline"
					:paint="draftLinePaint as any"
				/>
			</mgl-geo-json-source>

			<!-- The Signal the ledger (or a map click) has selected, marked above
			     everything: a halo and a ring around its own dot, at its own point.
			     The tile still draws the Signal; this only says which one is being
			     read, so the marker survives a change of effect colour underneath. -->
			<mgl-geo-json-source
				source-id="planning-signal-selected"
				:data="selectedSignalFC"
			>
				<mgl-circle-layer
					layer-id="planning-signal-selected-halo"
					:paint="selectedHaloPaint as any"
				/>
				<mgl-circle-layer
					layer-id="planning-signal-selected-ring"
					:paint="selectedRingPaint as any"
				/>
			</mgl-geo-json-source>

			<!-- The drawn ring comes back in as `area`, so an area selection stays
			     visible after the widget has dropped its own copy. -->
			<AreaDraw
				source-id="planning-area-draw"
				:modes="['rectangle', 'polygon']"
				:mode="mode === 'signal' ? null : areaMode"
				:committed="area ? [area] : []"
				@commit="onAreaCommit"
				@cancel="emit('area-cancel')"
			/>

			<!-- Click resolution lives here, so painting and selecting never race:
			     while a Signal is being placed, or an area tool is armed, that
			     gesture owns every click; while painting without an area tool, a
			     click names the cell under it; otherwise it names the object.

			     The armed-area check is not restricted to paint mode: terra-draw's
			     rectangle and polygon modes CLOSE on a click, and that click is
			     delivered to this widget too. If it answers, it deselects — or
			     clears — the selection the draw has just made, and which of the two
			     runs first is only listener registration order. Disabling for the
			     whole gesture makes that order irrelevant, and the page disarms the
			     tool after the gesture, so the props have not changed yet when the
			     closing click arrives. -->
			<FeatureSelect
				:layers="planningSelectLayers"
				:disabled="mode === 'signal' || areaMode !== null"
				@select="onFeatureSelect"
				@clear="onFeatureClear"
			/>
		</mgl-map>
	</div>
</template>

<script setup lang="ts">
import "maplibre-gl/dist/maplibre-gl.css";

import { computed, ref, shallowRef, watch } from "vue";
import type { Map as MaplibreMap, MapMouseEvent } from "maplibre-gl";
import {
	MglMap,
	MglGeoJsonSource,
	MglFillLayer,
	MglLineLayer,
	MglCircleLayer,
	type MglEvent,
} from "vue-maplibre-gl";
import type { FeatureCollection, GeoJSON, Polygon } from "geojson";

import SourceServiceArea from "@/map/SourceServiceArea.vue";
import SourceTegola from "@/map/SourceTegola.vue";
import { ALIDADE_SMOOTH } from "@/map/constants";
import { boundsDefault, boundsFromGeoJSON, mapStyleURL } from "@/map/util";
import {
	cellBoundaryRing,
	cellsFeatureCollection,
	cellsInPolygon,
} from "@/lib/h3/cells";
import { authoringResForZoom } from "@/lib/h3/ladder";
import {
	AreaDraw,
	FeatureSelect,
	featuresInRing,
	type AreaCommit,
	type MapSelectPayload,
	type SelectId,
	type SelectLayer,
} from "@/map/select";

const props = withDefaults(
	defineProps<{
		activeKind: "signals" | "leads" | "work_orders";
		draftCells: string[];
		mode: "select" | "paint" | "signal";
		areaMode?: "rectangle" | "polygon" | null;
		canPaint: boolean;
		/** The ring in force, drawn back so the area selection stays visible. */
		area?: GeoJSON.Polygon | null;
		/** The resolution the draft is locked to, once its first cell was added.
		 * A Lead stores one resolution, so the grid stops re-cutting at the zoom
		 * the operator started painting at. Null means the grid follows the zoom. */
		lockedRes?: number | null;
		/** The saved Leads' cells, by cell index, with the state that decides
		 * whether an overlap is a warning (open) or context (closed). */
		existingLeadCells?: Map<string, "open" | "closed">;
		/** The Signals the workbench has selected: the map marks each one's point,
		 * so the rows in the ledger and the dots on the map are the same set. A
		 * Signal with no location carries none, and is not marked. */
		selectedSignals?: { latitude: number; longitude: number }[];
		/** Bumped by the page after a write that changes a visible footprint. */
		version: number;
	}>(),
	{
		area: null,
		areaMode: null,
		lockedRes: null,
		selectedSignals: () => [],
		existingLeadCells: () => new Map<string, "open" | "closed">(),
	},
);

const emit = defineEmits<{
	(e: "cell", cell: string, resolution: number): void;
	(e: "cells", cells: string[], resolution: number): void;
	(e: "signal-at", longitude: number, latitude: number): void;
	(e: "select", payload: MapSelectPayload): void;
	(e: "clear"): void;
	(
		e: "area",
		payload: { kind: string; ids: SelectId[]; ring: [number, number][] },
	): void;
	(e: "area-cancel"): void;
	(e: "update:mode", mode: "select" | "paint" | "signal"): void;
}>();

const center = ref<[number, number]>([-119.7, 36.8]);
const mapRef = shallowRef<MaplibreMap | null>(null);
const serviceArea = ref<GeoJSON.GeoJSON | null>(null);
/** The camera's zoom, which is what the authoring grid's resolution follows. */
const zoom = ref(6);
/** The viewport as a ring, so the grid covers exactly what is on screen. */
const viewportRing = ref<[number, number][]>([]);

/**
 * The resolution the authoring grid is cut at: the draft's own once its first
 * cell is added, otherwise the zoom's. A Lead stores one resolution, so a draft
 * that mixed cell sizes would be unsavable; locking at the first cell is what
 * makes the zoom a choice the operator makes once, at the start.
 */
const workingRes = computed(
	() => props.lockedRes ?? authoringResForZoom(zoom.value),
);

/**
 * The workbench's hit-testing policy, declared once. `FeatureSelect` tests
 * clicks and `featuresInRing` tests drawn areas against this same list, so the
 * two cannot disagree about what a Signal or a Lead is.
 *
 * The cell grid is only a target while painting. Outside paint mode a click
 * belongs to the object under it, which is what the ledger and the details pane
 * act on; inside paint mode the cell is the object, and it wins over the Signal
 * painted beneath it.
 */
const planningSelectLayers = computed<SelectLayer[]>(() => {
	const cells: SelectLayer[] =
		props.mode === "paint"
			? [
					{
						layerId: "planning-h3-hit",
						kind: "h3_cell",
						priority: 0,
						target: "point",
						idOf: (properties) =>
							typeof properties.cell === "string" ? properties.cell : null,
					},
				]
			: [];
	return [
		...cells,
		{ layerId: "planning-signals", kind: "signal", priority: 1 },
		{ layerId: "planning-lead-points", kind: "lead", priority: 2 },
		{
			layerId: "planning-lead-fill",
			kind: "lead",
			priority: 3,
			target: "point",
		},
		{
			layerId: "planning-work-order-fill",
			kind: "work_order",
			priority: 4,
			target: "point",
		},
	];
});

/** The ledger tab the rail has active, and the selection kind it stands for. */
const KIND_BY_TAB: Record<
	"signals" | "leads" | "work_orders",
	"signal" | "lead" | "work_order"
> = {
	signals: "signal",
	leads: "lead",
	work_orders: "work_order",
};

/**
 * The grid normally holds a few thousand cells: the authoring ladder keeps a
 * cell at least a fingertip wide, so the count is bounded by the viewport, not
 * by the data. This cap is a safety valve for a pathological viewport (a
 * degenerate camera, a very wide window) — an empty grid is a visible failure,
 * while a hundred thousand polygons would wedge the tab.
 */
const MAX_GRID_CELLS = 6000;

/** The cells the grid draws, at the resolution in force. */
const gridCells = computed<string[]>(() => {
	const ring = viewportRing.value;
	if (ring.length < 4) return [];
	const cells = cellsInPolygon(ring, workingRes.value);
	return cells.length > MAX_GRID_CELLS ? [] : cells;
});

const gridCellsFC = computed<FeatureCollection<Polygon>>(() =>
	cellsFeatureCollection(gridCells.value, () => ({})),
);

/** The draft's cells, as a set: membership is asked once per grid cell, so a
 * `Set` here is what keeps the cross-product linear. */
const draftCellSet = computed(() => new Set(props.draftCells));

/** The saved Leads' cells that the grid covers, with their state. Drawing only
 * what is on screen keeps this proportional to the grid, not to the district. */
const leadCellFeatures = computed<FeatureCollection<Polygon>>(() =>
	cellsFeatureCollection(gridCells.value, (cell) => {
		const state = props.existingLeadCells.get(cell);
		return state === undefined ? {} : { state };
	}),
);

/** Whether a cell of the draft is also a cell of an OPEN Lead — the overlap the
 * centre-map design calls a warning. A closed Lead's overlap is context, not a
 * conflict, so it is not marked. */
function overlapsOpenLead(cell: string): boolean {
	return props.existingLeadCells.get(cell) === "open";
}

const draftCellFeatures = computed<FeatureCollection<Polygon>>(() =>
	cellsFeatureCollection(props.draftCells, (cell) => ({
		draft: true,
		overlap: overlapsOpenLead(cell),
	})),
);

/** The selected Signals as points, or an empty collection when the workbench is
 * reading something else. Each one's own dot keeps its effect colour underneath;
 * the halo and the ring are what say "these". */
const selectedSignalFC = computed<FeatureCollection<GeoJSON.Point>>(() => ({
	type: "FeatureCollection",
	features: props.selectedSignals.map((selected) => ({
		type: "Feature" as const,
		properties: {},
		geometry: {
			type: "Point" as const,
			coordinates: [selected.longitude, selected.latitude],
		},
	})),
}));

/* A wash wide enough to find at the district zoom, and a dark ring that reads
   over any basemap and over the Signal's own effect colour. */
const selectedHaloPaint = {
	"circle-color": "#0d6efd",
	"circle-opacity": 0.18,
	"circle-radius": 16,
};

const selectedRingPaint = {
	"circle-color": "#ffffff",
	"circle-opacity": 0,
	"circle-radius": 9,
	"circle-stroke-color": "#212529",
	"circle-stroke-width": 2.5,
	"circle-stroke-opacity": 0.9,
};

/* A Signal is the only planning object drawn as a point, and its dot must not be
   mistakable for a Lead's. A reviewed Signal is filled with its effect's colour —
   green promotes, red suppresses, grey is context — while a Signal that has not
   been reviewed is HOLLOW: a white disc with a blue ring, which reads as "no
   judgement yet" and cannot be confused with the solid blue dot a legacy Lead
   site draws. The tile sends `unreviewed` as the effect when the column is NULL,
   so one property carries both states. */
const signalPaint = {
	"circle-color": [
		"match",
		["get", "effect"],
		"promotive",
		"#198754",
		"suppressive",
		"#dc3545",
		"contextual",
		"#6c757d",
		"#ffffff",
	],
	"circle-radius": 5,
	"circle-stroke-color": [
		"match",
		["get", "effect"],
		"promotive",
		"#ffffff",
		"suppressive",
		"#ffffff",
		"contextual",
		"#ffffff",
		"#0d6efd",
	],
	"circle-stroke-width": [
		"case",
		["==", ["get", "effect"], "unreviewed"],
		2,
		1,
	],
};

const leadFillPaint = {
	"fill-color": ["match", ["get", "state"], "open", "#0d6efd", "#6c757d"],
	"fill-opacity": [
		"case",
		["==", ["get", "state"], "closed"],
		0.1,
		["==", ["get", "priority"], "high"],
		0.35,
		0.22,
	],
};

const leadLinePaint = {
	"line-color": ["match", ["get", "state"], "open", "#0d6efd", "#6c757d"],
	"line-width": 1.5,
	"line-opacity": 0.9,
};

const leadPointPaint = {
	"circle-color": ["match", ["get", "state"], "open", "#0d6efd", "#6c757d"],
	"circle-radius": 5,
	"circle-stroke-color": "#ffffff",
	"circle-stroke-width": 1,
};

const workOrderFillPaint = {
	"fill-color": "#fd7e14",
	"fill-opacity": 0.16,
};

const workOrderLinePaint = {
	"line-color": "#b35c00",
	"line-width": 1,
	"line-dasharray": [2, 2],
	"line-opacity": 0.7,
};

/* The authoring grid is a reference, not content: a hairline that disappears
   behind the footprints it is painted over. */
const gridLinePaint = {
	"line-color": "#adb5bd",
	"line-width": 0.5,
	"line-opacity": 0.55,
};

/* The hit target. Invisible, but it must be a fill: an outline has no interior
   to query, so a click in the middle of a hexagon would miss. */
const hitPaint = {
	"fill-color": "#ffffff",
	"fill-opacity": 0,
};

/* A saved Lead's cells, in the state's own colour at a wash light enough to read
   the grid through. An open Lead's blue is the warning colour an overlapping
   draft inherits. */
const leadCellFillPaint = {
	"fill-color": ["match", ["get", "state"], "open", "#0d6efd", "#6c757d"],
	"fill-opacity": 0.1,
};

const leadCellLinePaint = {
	"line-color": ["match", ["get", "state"], "open", "#0d6efd", "#6c757d"],
	"line-width": 1,
	"line-opacity": 0.6,
};

const draftFillPaint = {
	"fill-color": ["case", ["get", "overlap"], "#dc3545", "#ffc107"],
	"fill-opacity": 0.5,
};

const draftLinePaint = {
	"line-color": ["case", ["get", "overlap"], "#a71d2a", "#b8860b"],
	"line-width": 2,
};

function onServiceAreaLoaded(geometry: GeoJSON.GeoJSON | null) {
	serviceArea.value = geometry;
	fitMap(geometry);
}

function onLoad(event: MglEvent<"load">) {
	mapRef.value = event.map;
	event.map.on("moveend", noteCamera);
	event.map.on("click", onMapClick);
	// Every real click begins with a press, so the first press after a draw is
	// where selection answers again (see `swallowClosingClick`).
	event.map.on("mousedown", () => {
		swallowClosingClick = false;
	});
	noteCamera();
	fitMap(serviceArea.value);
}

/**
 * Placing a Signal is the one gesture with no layer to resolve: the operator is
 * naming empty ground, not picking something already on it. Every other mode
 * belongs to `FeatureSelect`, which is disabled while this one is active, so the
 * two never both answer the same click.
 */
function onMapClick(event: MapMouseEvent) {
	if (props.mode !== "signal") return;
	emit("signal-at", event.lngLat.lng, event.lngLat.lat);
}

/** Placing a Signal wants a crosshair: the pointer is naming ground, not reading
 * what is already drawn. The area tool owns the cursor while it is armed, and
 * gives it back on disarm, so this only speaks when neither is in force. */
watch(
	() => props.mode,
	(mode) => {
		const map = mapRef.value;
		if (!map || props.areaMode !== null) return;
		map.getCanvas().style.cursor = mode === "signal" ? "crosshair" : "";
	},
);

/**
 * Note the camera and the viewport the grid is cut from. `moveend` covers a pan
 * and a zoom alike (MapLibre fires it after `zoomend` too), and the grid is only
 * rebuilt while painting — `gridCells` is idle otherwise, so a select-mode pan
 * costs one bounds read.
 */
function noteCamera() {
	const map = mapRef.value;
	if (!map) return;
	zoom.value = map.getZoom();
	const bounds = map.getBounds();
	const west = bounds.getWest();
	const south = bounds.getSouth();
	const east = bounds.getEast();
	const north = bounds.getNorth();
	viewportRing.value = [
		[west, south],
		[east, south],
		[east, north],
		[west, north],
		[west, south],
	];
}

/**
 * True from the moment a draw commits until the operator next presses the map.
 *
 * terra-draw completes a rectangle on `mouseup`, and the browser dispatches the
 * `click` for that press as a SEPARATE task afterwards — by the time it arrives
 * the tool is already disarmed and this widget is live again. That click hits
 * empty map, emits `clear`, and wipes the selection the draw has just made; it
 * is the same press, so nothing should be answered from it.
 *
 * The flag is cleared by the next `mousedown`, not by a timer: every real click
 * begins with a press, so the first genuine interaction re-arms selection, and
 * a draw whose closing click never arrives (a keyboard-finished polygon) cannot
 * swallow anything.
 */
let swallowClosingClick = false;

/** A click resolved by the shared widget against the declared layers. A cell is
 * the page's vocabulary, not the selection store's: the draft footprint is
 * geometry being authored, so the cell is reported with the resolution it was
 * cut at and the page decides. */
function onFeatureSelect(payload: MapSelectPayload) {
	if (swallowClosingClick) return;
	if (payload.kind === "h3_cell") {
		if (!props.canPaint) return;
		emit("cell", String(payload.id), workingRes.value);
		return;
	}
	emit("select", payload);
}

/**
 * An empty-map click means "deselect" in select mode — the same answer a ledger
 * gives to clicking blank space. While painting it means nothing: the operator
 * is adding cells, and a click that missed the grid must not throw away the
 * row they were reading.
 */
function onFeatureClear() {
	if (swallowClosingClick) return;
	if (props.mode === "paint") return;
	emit("clear");
}

/** A drawn shape, in one of two meanings that depend on the mode.
 *
 * Painting: the ring is filled with the cells it covers, at the resolution in
 * force — the area is a brush, not a selection. Selecting: the ring names the
 * objects of the active type inside it, as before.
 */
function onAreaCommit({ ring }: AreaCommit) {
	const map = mapRef.value;
	if (!map) return;
	// The press that closed the draw also produces a `click` a task later, and
	// the selection that click would clear is the one this commit is about to
	// make.
	swallowClosingClick = true;
	if (props.mode === "paint") {
		if (!props.canPaint) return;
		emit("cells", cellsInPolygon(ring, workingRes.value), workingRes.value);
		return;
	}
	const kind = KIND_BY_TAB[props.activeKind];
	const specs = planningSelectLayers.value.filter(
		(layer) => layer.kind === kind,
	);
	const ids = featuresInRing(map, ring, specs).get(kind) ?? [];
	emit("area", { kind, ids, ring });
}

function fitMap(geometry: GeoJSON.GeoJSON | null) {
	const map = mapRef.value;
	if (!map) return;
	const bounds = geometry ? boundsFromGeoJSON(geometry) : null;
	if (bounds && !bounds.isEmpty()) {
		map.fitBounds(bounds, { padding: 40, duration: 600 });
	} else {
		map.fitBounds(boundsDefault(), { padding: 60, duration: 600 });
	}
}

/** A Signal's own zoom: the work at a place is drawn from a handful of parcels,
 * so the frame goes to the one the Signal stands on rather than staying at the
 * district view. The zoom floor matches the Operations map's own target framing,
 * and a camera already closer is left where it is — selecting a Signal must not
 * pull the operator out of the detail they were reading. */
const FOCUS_ZOOM = 14;

function focusOn(longitude: number, latitude: number): void {
	const map = mapRef.value;
	if (!map) return;
	map.easeTo({
		center: [longitude, latitude],
		zoom: Math.max(map.getZoom(), FOCUS_ZOOM),
		duration: 500,
	});
}

/** The most a footprint's fit may zoom to: one cell would otherwise fill the
 * pane, which loses the streets and neighbours the operator plans against. */
const FOOTPRINT_MAX_ZOOM = 16;

/**
 * A Lead is a cell set, so it has no single point to centre on: the camera fits
 * the cells' own bounds instead. The cells already draw, so this is the same
 * ground the operator is looking at, framed.
 *
 * A footprint with no cells (the legacy Leads that predate H3 and carry only a
 * Site) has nothing to frame and is left alone rather than guessed at.
 */
function focusOnCells(cells: string[]): void {
	const map = mapRef.value;
	if (!map || cells.length === 0) return;
	let west = Infinity;
	let south = Infinity;
	let east = -Infinity;
	let north = -Infinity;
	for (const cell of cells) {
		for (const [lng, lat] of cellBoundaryRing(cell)) {
			if (lng < west) west = lng;
			if (lng > east) east = lng;
			if (lat < south) south = lat;
			if (lat > north) north = lat;
		}
	}
	if (!Number.isFinite(west) || !Number.isFinite(north)) return;
	map.fitBounds(
		[
			[west, south],
			[east, north],
		],
		{ padding: 60, maxZoom: FOOTPRINT_MAX_ZOOM, duration: 500 },
	);
}

defineExpose({ focusOn, focusOnCells });
</script>
